import React, { useEffect, useState } from 'react';
import { cancelBooking, getListBooking } from '@/services/api/handovers';
import { showErrorToast } from '@/components/toast/globalToast';
import { extractApiErrorMessage, getUserEmail } from '@/utils';
import CreateBookingModal from './CreateBookingModal';
import AllCandidateBookingTab from './bookingTab/AllCandidateBookingTab';
import ActiveBookingTab from './bookingTab/ActiveBookingTab';
import { BookingMitraOption, BookingRow } from './bookingTab/shared';

interface BookingTabProps {
  onTotalChange?: (total: number) => void;
  refreshTrigger?: number;
  hubLocations: any[];
  vehicles: any[];
  oems: any[];
  models: any[];
  onMitraOptionsChange?: (options: BookingMitraOption[]) => void;
  onCreateHandover?: (record: BookingRow) => void;
}

const CancelBookingModal: React.FC<{
  isOpen: boolean;
  plateNumber?: string;
  isLoading?: boolean;
  onClose: () => void;
  onConfirm: () => void;
}> = ({ isOpen, plateNumber, isLoading = false, onClose, onConfirm }) => {
  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-[60] bg-black/45 backdrop-blur-[1px]" />
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
        <div className="h-[180px] w-[460px] overflow-hidden rounded-[24px] bg-white shadow-[0_24px_48px_rgba(17,24,39,0.22)]">
          <div className="px-6 pt-5 pb-4">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#FFF4EC]">
                <i className="ri-error-warning-fill text-[22px] text-[#FF7A1A]"></i>
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-[16px] leading-6 font-semibold text-[#2B2B2B]">
                  Cancel Vehicle Booking?
                </h3>
                <p className="mt-2 text-[14px] leading-5 text-[#6B7280]">
                  {plateNumber
                    ? `Confirms the specific plate number ${plateNumber} and warns that the vehicle will be released to IDLE?`
                    : 'Confirms the specific plate number and warns that the vehicle will be released to IDLE?'}
                </p>
              </div>
            </div>
          </div>

          <div className="border-t border-[#E5E7EB] px-6 py-4">
            <div className="flex justify-end gap-3">
              <button
                onClick={onClose}
                disabled={isLoading}
                className="w-[120px] rounded-xl border border-[#E5E7EB] bg-white p-2 text-[14px] font-medium text-[#6B7280] transition hover:bg-gray-50 disabled:opacity-50"
              >
                Keep Booking
              </button>
              <button
                onClick={onConfirm}
                disabled={isLoading}
                className="w-[130px] rounded-xl border border-transparent bg-[#FF4D5E] p-2 text-[14px] font-medium text-white transition hover:bg-[#F43F52] disabled:opacity-50"
              >
                {isLoading ? 'Cancelling...' : 'Cancel Booking'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const BookingTab: React.FC<BookingTabProps> = ({
  onTotalChange,
  refreshTrigger = 0,
  hubLocations,
  vehicles,
  oems,
  models,
  onMitraOptionsChange,
  onCreateHandover,
}) => {
  const [activeSubTab, setActiveSubTab] = useState<'candidate' | 'active'>(
    'candidate'
  );
  const [candidateTotal, setCandidateTotal] = useState(0);
  const [, setActiveTotal] = useState(0);
  const [activeBookingTotal, setActiveBookingTotal] = useState(0);
  const [mitraOptions, setMitraOptions] = useState<BookingMitraOption[]>([]);
  const [editingBooking, setEditingBooking] = useState<BookingRow | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<BookingRow | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [localRefreshNonce, setLocalRefreshNonce] = useState(0);
  const combinedRefreshTrigger = refreshTrigger + localRefreshNonce;

  useEffect(() => {
    onTotalChange?.(activeBookingTotal);
  }, [activeBookingTotal, onTotalChange]);

  useEffect(() => {
    const loadActiveBookingTotal = async () => {
      try {
        const response = await getListBooking({
          page: '1',
          size: '10',
          statuses: 'WAITING,NO_SHOW',
        });

        setActiveBookingTotal(response.pagination?.total || 0);
      } catch (error) {
        const errorMessage = extractApiErrorMessage(
          error,
          'Failed to load booking total'
        );
        showErrorToast('Error', errorMessage);
        setActiveBookingTotal(0);
      }
    };

    loadActiveBookingTotal();
  }, [combinedRefreshTrigger]);

  const handleMitraOptionsChange = (options: BookingMitraOption[]) => {
    setMitraOptions(options);
    onMitraOptionsChange?.(options);
  };

  const handleEditBooking = (record: BookingRow) => {
    setEditingBooking(record);
    setIsEditModalOpen(true);
  };

  const handleCancelBooking = (record: BookingRow) => {
    setCancelTarget(record);
  };

  const handleConfirmCancel = async () => {
    if (!cancelTarget?.bookingID) return;

    try {
      setIsCancelling(true);
      await cancelBooking(cancelTarget.bookingID, {
        updatedBy: getUserEmail(),
      });
      setCancelTarget(null);
      setLocalRefreshNonce((prev) => prev + 1);
    } catch (error) {
      const errorMessage = extractApiErrorMessage(
        error,
        'Failed to cancel booking'
      );
      showErrorToast('Error', errorMessage);
    } finally {
      setIsCancelling(false);
    }
  };

  const editingMitraOption = editingBooking
    ? {
        value: editingBooking.driverID?.toString() || editingBooking.id,
        label: `${editingBooking.mitraCode} - ${editingBooking.mitraName}`,
        data: {
          driverID: editingBooking.driverID,
          driverName: editingBooking.mitraName,
          driverCode: editingBooking.mitraCode,
        },
      }
    : undefined;

  return (
    <>
      <div className="space-y-4">
        <div className="rounded-xl border border-gray-200 p-1 bg-gray-50 flex gap-1">
          <button
            onClick={() => setActiveSubTab('candidate')}
            className={`flex-1 rounded-lg py-1 text-sm font-medium transition-colors ${
              activeSubTab === 'candidate'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            All Candidate{candidateTotal > 0 ? ` (${candidateTotal})` : ''}
          </button>
          <button
            onClick={() => setActiveSubTab('active')}
            className={`flex-1 rounded-lg py-1 text-sm font-medium transition-colors ${
              activeSubTab === 'active'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Active Booking
            {activeBookingTotal > 0 ? ` (${activeBookingTotal})` : ''}
          </button>
        </div>

        {activeSubTab === 'candidate' ? (
          <AllCandidateBookingTab
            refreshTrigger={combinedRefreshTrigger}
            onTotalChange={setCandidateTotal}
            onMitraOptionsChange={handleMitraOptionsChange}
          />
        ) : (
          <ActiveBookingTab
            refreshTrigger={combinedRefreshTrigger}
            hubLocations={hubLocations}
            vehicles={vehicles}
            oems={oems}
            models={models}
            onTotalChange={setActiveTotal}
            onCreateHandover={(record) => onCreateHandover?.(record)}
            onEditBooking={handleEditBooking}
            onCancelBooking={handleCancelBooking}
          />
        )}
      </div>

      <CreateBookingModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingBooking(null);
        }}
        onSuccess={() => {
          setIsEditModalOpen(false);
          setEditingBooking(null);
          setLocalRefreshNonce((prev) => prev + 1);
        }}
        hubLocations={hubLocations}
        vehicles={vehicles}
        mitraOptions={mitraOptions}
        mode="edit"
        bookingId={editingBooking?.bookingID}
        initialMitraId={editingMitraOption?.value}
        initialMitraLabel={editingMitraOption?.label}
        initialData={
          editingBooking
            ? {
                hubLocationID: editingBooking.hubLocationID || '',
                hubLocationName: editingBooking.hubLocationName || '',
                businessUnit: editingBooking.businessUnit || '',
                pickupDate: editingBooking.bookingDate || '',
                pickupTime:
                  editingBooking.startTime && editingBooking.endTime
                    ? `${editingBooking.startTime} - ${editingBooking.endTime}`
                    : '',
                vehicleID: editingBooking.vehicleID || '',
                vehiclePlateNumber: editingBooking.vehiclePlateNumber || '',
                oemName: editingBooking.oemName || '',
                modelName: editingBooking.modelName || '',
                vehicleLabel: editingBooking.vehicleLabel || '',
                mitraCode: editingBooking.mitraCode || '',
                mitraName: editingBooking.mitraName || '',
                mitraID: editingMitraOption?.value || '',
                mitraLabel: editingMitraOption?.label || '',
                mitraData: editingMitraOption?.data,
              }
            : undefined
        }
      />

      <CancelBookingModal
        isOpen={!!cancelTarget}
        plateNumber={cancelTarget?.vehiclePlateNumber}
        isLoading={isCancelling}
        onClose={() => setCancelTarget(null)}
        onConfirm={handleConfirmCancel}
      />
    </>
  );
};

export default BookingTab;
